import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import HeadSub from '../../../../components/comm/HeadSub';
import ConfirmModal from '../../../../components/comm/ConfirmModal';
import FoodItemHeaderCard from '../../../../components/home/food-record/FoodItemHeaderCard';
import NutritionInfoCard from '../../../../components/home/food-record/NutritionInfoCard';
import QuantityAdjuster from '../../../../components/home/food-record/QuantityAdjuster';
import URL from '../../../../constants/url';
import { loadMeal, saveMeal } from '../../../../utils/mealStorage';
import { delImage } from '../../../../utils/imageStore';

import style from './FoodItemDetail.module.css';
import axiosApi from '../../../../api/daonAxios';
import { IMAGE_URL } from '../../../../constants/url';

const EMPTY_NUTRITION = { kcal: 0, sodium: 0, protein: 0, carb: 0, fat: 0 };

const CROP_PADDING_RATIO = 0.15;

// 음식 영역 크롭
const cropFoodImage = async (file, food, jToken) => {
    const xmin = Number(food?.positionXmin);
    const ymin = Number(food?.positionYmin);
    const xmax = Number(food?.positionXmax);
    const ymax = Number(food?.positionYmax);

    if (!file || !(xmax > xmin) || !(ymax > ymin)) {
        return null;
    }

    const res = await axiosApi.get(
        IMAGE_URL + `${file.atchFileStr}&fileSn=${file.fileSn}`,
        { responseType: 'blob', headers: { Authorization: `Bearer ${jToken}` } },
    );
    const objectUrl = window.URL.createObjectURL(res.data);

    try {
        const image = await new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = objectUrl;
        });

        const padX = (xmax - xmin) * CROP_PADDING_RATIO;
        const padY = (ymax - ymin) * CROP_PADDING_RATIO;
        const sx = Math.max(0, xmin - padX);
        const sy = Math.max(0, ymin - padY);
        const sw = Math.min(image.naturalWidth, xmax + padX) - sx;
        const sh = Math.min(image.naturalHeight, ymax + padY) - sy;

        const canvas = document.createElement('canvas');
        canvas.width = sw;
        canvas.height = sh;
        canvas.getContext('2d').drawImage(image, sx, sy, sw, sh, 0, 0, sw, sh);

        return canvas.toDataURL('image/jpeg', 0.9);
    } finally {
        window.URL.revokeObjectURL(objectUrl);
    }
};

const DEFAULT_UNIT_OPTIONS = ['개', 'g', 'ml', '인분'];

/**
 * 저장소에 들어있는 촬영 결과 항목을 이 화면이 쓰는 형태로 변환.
 * 저장 항목의 영양값은 baseQty 기준 절대값이다 (foodLensMapper 참조).
 */
const fromStoredItem = (it) => ({
    id: it.id,
    name: it.name,
    baseQty: Number(it.baseQty) || 1,
    baseUnit: it.baseUnit || '개',
    baseAmount: it.baseAmount || '',
    // SDK 는 음식별 코멘트를 주지 않는다. 서버 AI 분석이 붙기 전까지는 비워둔다.
    aiAnalysis: '',
    nutrition: {
        kcal:    Number(it.kcal)    || 0,
        sodium:  Number(it.sodium)  || 0,
        protein: Number(it.protein) || 0,
        carb:    Number(it.carb)    || 0,
        fat:     Number(it.fat)     || 0,
    },
    importantKeys: [],
});

const FoodItemDetail = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const location = useLocation();
    const { state } = location;

    const foodImageUrl = state?.imageUrl ?? null;

    const [calorRecDtl, setCalorRecDtl] = useState();
    const [cropImageUrl, setCropImageUrl] = useState(null);

    const [deleteOpen, setDeleteOpen] = useState(false);

    const calorRecId = searchParams.get('calorRecId') || 0;
    const foodLensId = Number(searchParams.get('foodLensId')) || 0;
    const activeSlideIndex = searchParams.get('activeSlideIndex') || 0;
    const ntktmTyCd = searchParams.get('ntktmTyCd') || 'BKFST';
    const recDe = searchParams.get('recDe') || dayjs().format('YYYYMMDD');

    useEffect(() => {

        if (!calorRecId || !foodLensId) {
            return;
        }

        getFoodItemDetail(calorRecId, foodLensId);

    }, [calorRecId, foodLensId]);


    const getFoodItemDetail = async (calorRecId, foodLensId) => {

        const jToken = localStorage.getItem('jToken');

        try {

            const res = await axiosApi.get(
                '/diet/toDayCalorHistDtl',
                {
                    params: {
                        calorRecId
                    },
                    headers: {
                        Authorization: `Bearer ${jToken}`
                    }
                }
            );

            if (res.status !== 200) {
                return;
            }

            const calorRec = res.data.result;

            if (!calorRec) {
                return;
            }

            /*
            * calorRec에는 여러 음식이 있으므로
            * 현재 FoodItemDetail에 해당하는 음식만 조회
            */
            const food = calorRec.foodLensList?.find(
                item => Number(item.foodLensId) === Number(foodLensId)
            );

            if (!food) {
                console.error(
                    '음식 정보를 찾을 수 없습니다.',
                    {
                        calorRecId,
                        foodLensId
                    }
                );
                return;
            }

            // 현재는 첫 번째 후보 사용
            const candidate =
                food.foodLensCnddteList?.[0] ?? null;

            // 첫 번째 수량 상세 사용
            const countDetail =
                candidate?.foodLensCnddteDtlList?.[0] ?? null;

            const newCalorRecDtl = {

                id: food.foodLensId,
                foodLensId: food.foodLensId,
                calorRecId: calorRec.id,

                name: food.name,
                fullName: food.fullName,

                foodName:
                    candidate?.foodName ??
                    food.name ??
                    '',

                counts:
                    candidate?.counts ??
                    countDetail?.counts ??
                    '',

                eatAmount:
                    food.eatAmount ?? 0,

                servingSize:
                    candidate?.servingSize ??
                    countDetail?.servingSize ??
                    0,

                unit:
                    candidate?.unit ?? '',

                energy:
                    // Number(candidate?.energy) || 0,
                    Math.round(Number(candidate.energy ?? 0)),

                sodium:
                    // Number(candidate?.sodium) || 0,
                    Math.round(Number(candidate?.sodium ?? 0)),

                protein:
                    // Number(candidate?.protein) || 0,
                    Math.round(Number(candidate?.protein ?? 0) * 10) / 10,

                carbohydrate:
                    // Number(candidate?.carbohydrate) || 0,
                    Math.round(Number(candidate?.carbohydrate ?? 0) * 10) / 10,

                fat:
                    // Number(candidate?.fat) || 0,
                    Math.round(Number(candidate?.fat ?? 0) * 10) / 10,

                candidate,
                countDetail,

                estimated:
                    candidate?.foodLensCnddteEstmtd ?? null,

                foodLens: food
            };

            //console.log(candidate)

            const cropped = await cropFoodImage(calorRec.fileList?.[0], food, jToken)
                .catch((error) => {
                    console.error('음식 이미지 크롭 실패:', error);
                    return null;
                });
            setCropImageUrl(cropped);

            setCalorRecDtl(newCalorRecDtl);

        } catch (error) {

            console.error(
                '음식 상세정보 조회 실패:',
                error
            );
        }
    };


    /////////////////////////////아래는 테스코드임



    const handleSave = useCallback(() => {
        
        const sendData = {         
            foodLensId : calorRecDtl.foodLensId,
            cnddteId :calorRecDtl.candidate.cnddteId,
            eatAmount : calorRecDtl.eatAmount,  
            servingSize : calorRecDtl.servingSize,
            energy : calorRecDtl.energy,
            sodium : calorRecDtl.sodium,
            protein : calorRecDtl.protein,
            carbohydrate : calorRecDtl.carbohydrate,
            fat : calorRecDtl.fat,
        }

        console.log(sendData)

        updateFoodLensCnddte(sendData)

    },[calorRecDtl]);

    const updateFoodLensCnddte = (sendData) => {
        const url = "/diet/foodLensInfo";
        const jToken = localStorage.getItem('jToken');
        const reqOptions = { headers: { Authorization: `Bearer ${jToken}` } };

        axiosApi.put(url, sendData, reqOptions)
        .then((res) => {
            if(res.status === 200) {
                navigate(-1);
            } else {
            }
        })
        .catch(err => {
            console.error("", err);
        })
    };

    const deleteFoodLens = (foodLensId) => {
        const url = "/diet/foodLens";
        const jToken = localStorage.getItem('jToken');
        const reqOptions = { 
            data : {foodLensId : foodLensId},
            headers: { Authorization: `Bearer ${jToken}` } 
        };
        axiosApi.delete(url,reqOptions)
        .then((res) => {
            if(res.status === 200) {
                navigate(-1);
            } else {
            }
        })
        .catch(err => {
            console.error("", err);
        })
    };

    const handleReplace = (foodLensId) => {
        navigate(URL.FOOD_SEARCH, {state: {foodLensId : foodLensId, saveGbn : "U", ntktmTyCd : ntktmTyCd, recDe : recDe}});
    };

    const handleConfirmDelete = (foodLensId) => {
        deleteFoodLens(foodLensId);
    };
    

    if (!calorRecDtl) {
        return null;
    }

    return (
        <>
            <HeadSub title="음식 상세" />

            <div id="sub-content">
                <div className="inner-wrap">
                    <div className={style.section}>
                        <FoodItemHeaderCard
                            name={calorRecDtl.foodName}
                            qty={`${calorRecDtl.eatAmount} ${calorRecDtl.counts} (${calorRecDtl.servingSize}${calorRecDtl.unit})`}
                            // aiAnalysis={"ai 분석 내용 들어가야함"}
                            cnddteId={calorRecDtl.candidate?.id}
                            image={cropImageUrl ?? foodImageUrl}
                        />
                    </div>

                    <div className={style.section}>
                        <NutritionInfoCard calorRecDtl={calorRecDtl} />
                    </div>

                    <div className={style.section}>
                        <QuantityAdjuster
                            calorRecDtl={calorRecDtl}
                            // value={value}
                            // onChange={setValue}
                            // unit={unit}
                            // onChangeUnit={handleChangeUnit}
                            setCalorRecDtl={setCalorRecDtl}
                        />
                    </div>

                    <button
                        type="button"
                        className={style.deleteBtn}
                        onClick={() => setDeleteOpen(true)}
                    >
                        <span className={style.trashIcon} aria-hidden="true" />
                        음식 삭제
                    </button>
                </div>
            </div>

            <div className={style.bottomFixed}>
                <button
                    type="button"
                    className={`${style.bottomBtn} ${style.btnSecondary}`}
                    onClick={() => {handleReplace(calorRecDtl.foodLensId)}}
                >
                    다른 음식으로 변경
                </button>
                <button
                    type="button"
                    className={`${style.bottomBtn} ${style.btnPrimary}`}
                    onClick={handleSave}
                >
                    기록하기
                </button>
            </div>

            <ConfirmModal
                open={deleteOpen}
                title="음식 삭제"
                message={<>'<b>{calorRecDtl.foodName}</b>'을<br />삭제하시겠습니까?</>}
                confirmText="삭제"
                cancelText="취소"
                onConfirm={() => {handleConfirmDelete(calorRecDtl.foodLensId)}}
                onCancel={() => setDeleteOpen(false)}
            />
        </>
    );
};

export default FoodItemDetail;
